import { ComponentType, createElement, PropsWithChildren, ReactNode } from 'react';

/** Provider used without a tuple — only `children` are passed. */
type ChildOnlyProvider = ComponentType<PropsWithChildren>;

/** A child-only provider, or a `[Provider, props]` tuple when extra props are required. */
export type ProviderEntry =
    ChildOnlyProvider | readonly [ComponentType<PropsWithChildren<any>>, Record<string, unknown>];

/**
 * Wraps children with multiple context providers in declaration order (outermost first).
 */
interface Props {
    providers: ProviderEntry[];
    children: ReactNode;
}

function isProviderTuple(
    entry: ProviderEntry,
): entry is readonly [ComponentType<PropsWithChildren<any>>, Record<string, unknown>] {
    return Array.isArray(entry);
}

function renderProviderEntry(entry: ProviderEntry, children: ReactNode): ReactNode {
    if (isProviderTuple(entry)) {
        const [Provider, providerProps] = entry;
        return createElement(Provider, providerProps, children);
    }

    return createElement(entry, null, children);
}

export function WithProviders(props: Props) {
    const { providers = [], children } = props;

    return (
        <>
            {providers.reduceRight(
                (acc, entry) => renderProviderEntry(entry, acc),
                children as ReactNode,
            )}
        </>
    );
}
